<script setup>
import TvTopBar from '~/components/tv/TvTopBar.vue';
import TvPanelFrame from '~/components/tv/TvPanelFrame.vue';
import TvTicker from '~/components/tv/TvTicker.vue';
import TvResultSting from '~/components/tv/TvResultSting.vue';
import TvSplashPanel from '~/components/tv/panels/TvSplashPanel.vue';
import TvLivePanel from '~/components/tv/panels/TvLivePanel.vue';
import TvResultsColumn from '~/components/tv/TvResultsColumn.vue';
import TvComingUpColumn from '~/components/tv/TvComingUpColumn.vue';
import TvClubJoinCard from '~/components/tv/TvClubJoinCard.vue';
import TvStandingsPanel from '~/components/tv/panels/TvStandingsPanel.vue';
import TvLadderPanel from '~/components/tv/panels/TvLadderPanel.vue';
import TvDrawPanel from '~/components/tv/panels/TvDrawPanel.vue';
import TvAmericanoPanel from '~/components/tv/panels/TvAmericanoPanel.vue';
import TvMeetPanel from '~/components/tv/panels/TvMeetPanel.vue';
import TvChallengesPanel from '~/components/tv/panels/TvChallengesPanel.vue';
import TvLeaderboardPanel from '~/components/tv/panels/TvLeaderboardPanel.vue';
import TvMatchReportsPanel from '~/components/tv/panels/TvMatchReportsPanel.vue';
import TvGridPanel from '~/components/tv/panels/TvGridPanel.vue';
import {TV_PANEL, TV_PANEL_ICON, tvOnAccent, tvScreens} from '~/utils/tvDisplay';

// The screen itself, given a view. The /tv/:key page fetches the view and keeps
// it live; the Clubhouse TV marketing page feeds it a demo. Nothing in here
// knows where the view came from.
const props = defineProps({
  view: {type: Object, default: null},
  connected: {type: Boolean, default: false},
  // Portrait reflow; when unset, follows the tv layout's provide()
  portrait: {type: Boolean, default: null},
  // Where the result sting draws its confetti (unset: over the document)
  confettiCanvas: {type: Object, default: null},
});
const view = computed(() => props.view);
const connected = computed(() => props.connected);
const injectedPortrait = inject('tvPortrait', ref(false));
const portrait = computed(() => props.portrait ?? injectedPortrait.value);

// ── Results queue: each one plays the sting once ──────────────────────────
const sting = ref(null);
const stingQueue = [];
const stung = new Set();
const freshIds = ref(new Set());

// Called by whoever owns the data when a result lands (the page from the hub,
// the demo from a button): plays the sting once per result.
const celebrate = (row, {confirmed} = {}) => {
  freshIds.value = new Set([...freshIds.value, row.fixtureId]);
  const once = `${row.fixtureId}:${confirmed}:${row.homeResult}-${row.awayResult}`;
  if (stung.has(once) || !view.value?.celebrateResults) return;
  stung.add(once);
  stingQueue.push({result: row, confirmed});
  if (!sting.value) nextSting();
};

const nextSting = () => {
  sting.value = stingQueue.shift() ?? null;
  if (sting.value) pauseRotation();
  else resumeRotation();
};

defineExpose({celebrate});

// ── Theme ──────────────────────────────────────────────────────────────────
const rgb = (hex) => {
  const n = parseInt((hex || '#D32946').replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mix = (hex, target, amount) => {
  const [r, g, b] = rgb(hex);
  const t = target === 'white' ? 255 : 0;
  const m = (c) => Math.round(c + (t - c) * amount);
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
};

const themeVars = computed(() => {
  const accent = view.value?.accentColour || '#D32946';
  const [r, g, b] = rgb(accent);
  const dark = view.value?.theme === 1;
  const clubhouse = view.value?.style === 1;
  const shared = {
    '--tv-accent': accent,
    '--tv-on-accent': tvOnAccent(accent),
    '--tv-accent-tint': `rgba(${r}, ${g}, ${b}, ${dark ? 0.22 : 0.1})`,
    // Broadcast numerals are condensed; Clubhouse uses the brand typeface throughout
    '--tv-display': clubhouse ? "'Poppins', sans-serif" : "'Barlow Condensed', sans-serif",
  };
  if (clubhouse) {
    return dark
        ? {
          ...shared,
          '--tv-bg': '#121821',
          '--tv-panel': '#1b232e',
          '--tv-bar': 'transparent',
          '--tv-line': '#2a3442',
          '--tv-ink': '#f1f4f8',
          '--tv-muted': '#95a0b0',
          '--tv-chip': '#232d3a',
          '--tv-lead': `rgba(${r}, ${g}, ${b}, 0.2)`,
          '--tv-accent-text': mix(accent, 'white', 0.35),
          '--tv-sting-card': '#1b232e',
          '--tv-sting-ink': '#ffffff',
          '--tv-sting-muted': '#95a0b0',
          '--tv-sting-line': '#2a3442',
          '--tv-sting-score-bg': '#232d3a',
        }
        : {
          ...shared,
          '--tv-bg': '#f1f3f6',
          '--tv-panel': '#ffffff',
          '--tv-bar': 'transparent',
          '--tv-line': '#e7eaef',
          '--tv-ink': '#16191f',
          '--tv-muted': '#6a7280',
          '--tv-chip': '#f3f5f8',
          '--tv-lead': `rgba(${r}, ${g}, ${b}, 0.08)`,
          '--tv-accent-text': mix(accent, 'black', 0.1),
          '--tv-sting-card': '#ffffff',
          '--tv-sting-ink': '#16191f',
          '--tv-sting-muted': '#6a7280',
          '--tv-sting-line': '#e7eaef',
          '--tv-sting-score-bg': '#f3f5f8',
        };
  }
  return dark
      ? {
        ...shared,
        '--tv-bg': '#0f1b2a',
        '--tv-panel': '#162536',
        '--tv-bar': '#1b2d42',
        '--tv-line': '#2a3d54',
        '--tv-ink': '#eef2f7',
        '--tv-muted': '#8fa0b5',
        '--tv-chip': '#20334a',
        '--tv-lead': `rgba(${r}, ${g}, ${b}, 0.16)`,
        '--tv-accent-text': mix(accent, 'white', 0.35),
        '--tv-sting-card': '#0f1b2a',
        '--tv-sting-ink': '#ffffff',
        '--tv-sting-muted': '#8fa0b5',
        '--tv-sting-line': '#24364b',
        '--tv-sting-score-bg': '#162536',
      }
      : {
        ...shared,
        '--tv-bg': '#e6eaf0',
        '--tv-panel': '#ffffff',
        '--tv-bar': '#f5f7fa',
        '--tv-line': '#dce1e8',
        '--tv-ink': '#14181f',
        '--tv-muted': '#5d6673',
        '--tv-chip': '#eef1f5',
        '--tv-lead': `rgba(${r}, ${g}, ${b}, 0.07)`,
        '--tv-accent-text': mix(accent, 'black', 0.1),
        '--tv-sting-card': '#ffffff',
        '--tv-sting-ink': '#14181f',
        '--tv-sting-muted': '#5d6673',
        '--tv-sting-line': '#dce1e8',
        '--tv-sting-score-bg': '#eef1f5',
      };
});

// The letterbox around a non-16:9 screen follows the theme
watchEffect(() => {
  if (import.meta.client && document.documentElement.classList.contains('tl-tv')) {
    document.documentElement.style.setProperty('--tv-letterbox', themeVars.value['--tv-bg']);
  }
});

// ── Sidebar ────────────────────────────────────────────────────────────────
// Latest results and coming up stay on screen; they don't rotate
const SIDEBAR_KINDS = [TV_PANEL.RecentResults, TV_PANEL.ComingUp];
const upcoming = computed(() => view.value?.panels?.find(p => p.kind === TV_PANEL.ComingUp)?.upcoming ?? []);
// Coming up only takes room when there's something booked; results fill the rest
const showsComingUp = computed(() => upcoming.value.length > 0);


// ── Rotation (the feature area) ────────────────────────────────────────────
// Pinned panels come round again after every three others. The club welcome
// only shows when there's nothing else to rotate.
const sequence = computed(() => {
  const all = (view.value?.panels ?? [])
      .filter(p => !SIDEBAR_KINDS.includes(p.kind))
      .filter(p => p.kind !== TV_PANEL.LiveNow || view.value?.live?.length);
  const competitions = all.filter(p => p.kind !== TV_PANEL.Splash);
  const panels = competitions.length ? competitions : all;
  const pinned = panels.filter(p => p.pinned);
  const regular = panels.filter(p => !p.pinned);
  if (!pinned.length || !regular.length) return panels;
  const out = [];
  let pin = 0;
  regular.forEach((panel, i) => {
    out.push(panel);
    if ((i + 1) % 3 === 0 || i === regular.length - 1) out.push(pinned[pin++ % pinned.length]);
  });
  return out;
});

const slot = ref(0);
const page = ref(0);
const tick = ref(0); // restarts the progress bar
let timer = null;
let paused = false;

// Short panels are packed several to a screen; the rotation steps through screens
const screens = computed(() => tvScreens(sequence.value, view.value, portrait.value));
const screen = computed(() => {
  const list = screens.value;
  return list.length ? list[slot.value % list.length] : null;
});
const screenPanels = computed(() => (screen.value?.columns ?? []).flat());
const current = computed(() => screenPanels.value[0] ?? null);
const pageCount = computed(() => screen.value?.pages ?? 1);
const screenKey = computed(() => screenPanels.value.map(p => p.key).join('+'));
// A packed screen stays up a little longer: there's more to read
const seconds = computed(() => {
  const panels = screenPanels.value;
  const base = Math.max(...panels.map(p => p.seconds || view.value?.defaultPanelSeconds || 15), 0);
  // A match report is read, not glanced at: at least 20 seconds each
  if (panels.some(p => p.kind === TV_PANEL.MatchReports)) return Math.max(base, 20);
  return base + (panels.length - 1) * 5;
});

const schedule = () => {
  clearTimeout(timer);
  if (paused || !current.value) return;
  tick.value++;
  timer = setTimeout(advance, seconds.value * 1000);
};

const advance = () => {
  // A hidden page (TV browser in the background, a covered window) doesn't turn
  // over: browsers pause animation frames there, and transitions queued up while
  // hidden can stall. It carries on when shown again.
  if (document.hidden) {
    schedule();
    return;
  }
  if (page.value + 1 < pageCount.value) {
    page.value++;
  } else {
    page.value = 0;
    slot.value = (slot.value + 1) % Math.max(1, screens.value.length);
  }
  schedule();
};

function pauseRotation() {
  paused = true;
  clearTimeout(timer);
}

function resumeRotation() {
  paused = false;
  schedule();
}

// A refetch can add, drop or reorder panels: stay on the one showing if it's still there
watch(() => screens.value.map(sc => sc.columns.flat().map(p => p.key).join('+')).join('|'), (now, before) => {
  if (now === before) return;
  const showingKey = current.value?.key;
  const index = screens.value.findIndex(sc => sc.columns.flat().some(p => p.key === showingKey));
  if (index >= 0) slot.value = index;
  else page.value = 0;
  if (page.value >= pageCount.value) page.value = 0;
});

onMounted(schedule);
onUnmounted(() => clearTimeout(timer));

const panelComponent = (kind) => ({
  [TV_PANEL.Splash]: TvSplashPanel,
  [TV_PANEL.LiveNow]: TvLivePanel,
  [TV_PANEL.LeagueStandings]: TvStandingsPanel,
  [TV_PANEL.Ladder]: TvLadderPanel,
  [TV_PANEL.TournamentDraw]: TvDrawPanel,
  [TV_PANEL.AmericanoStandings]: TvAmericanoPanel,
  [TV_PANEL.Meet]: TvMeetPanel,
  [TV_PANEL.Challenges]: TvChallengesPanel,
  [TV_PANEL.Leaderboard]: TvLeaderboardPanel,
  [TV_PANEL.MatchReports]: TvMatchReportsPanel,
  [TV_PANEL.LeagueGrid]: TvGridPanel,
}[kind]);

const panelProps = (panel) => {
  switch (panel.kind) {
    case TV_PANEL.Splash: return {panel, clubName: view.value.clubName, logo: view.value.clubImageFileName, running: running.value};
    case TV_PANEL.LiveNow: return {matches: view.value.live, page: page.value};
    default: return {panel, page: page.value};
  }
};

// One row per competition on the screen (a league's divisions count once)
const COMPETITION_KINDS = [TV_PANEL.LeagueStandings, TV_PANEL.LeagueGrid, TV_PANEL.Ladder, TV_PANEL.TournamentDraw, TV_PANEL.AmericanoStandings, TV_PANEL.Meet];
const running = computed(() => {
  const seen = new Map();
  for (const panel of view.value?.panels ?? []) {
    if (COMPETITION_KINDS.includes(panel.kind) && !seen.has(panel.targetId)) {
      seen.set(panel.targetId, {title: panel.title, icon: TV_PANEL_ICON[panel.kind]});
    }
  }
  return [...seen.values()];
});

const liveCount = computed(() => (view.value?.showLiveScores ? view.value?.live?.length : 0) ?? 0);
</script>

<template>
  <div class="tv-screen" :class="{'tv-screen--clubhouse': view?.style === 1}" :style="themeVars">
    <template v-if="view">
      <tv-top-bar
          :club-name="view.clubName"
          :logo="view.clubImageFileName"
          :live-count="liveCount"
          :connected="connected"
          :dark-bar="view.theme === 1"
      />
      <div class="tv-progress-track">
        <div v-if="current" :key="tick" class="tv-progress" :style="{animationDuration: seconds + 's'}"/>
      </div>
      <div class="tv-body">
        <main class="tv-feature">
          <!-- Fixed durations: if a screen changes again mid-transition, Vue finishes on a
               timer rather than waiting for a transitionend that never comes (which left
               the feature area blank) -->
          <transition name="tv-swap" mode="out-in" :duration="{enter: 560, leave: 460}">
            <div v-if="screen" :key="screenKey + ':' + page" class="tv-screenful" :class="`tv-screenful--${screen.columns.length}`">
              <div v-for="(column, c) in screen.columns" :key="c" class="tv-column">
                <tv-panel-frame
                    v-for="panel in column"
                    :key="panel.key"
                    :panel="panel"
                    :mode="screen.mode"
                    :page="page"
                    :pages="pageCount"
                    :component="panelComponent(panel.kind)"
                    :component-props="panelProps(panel)"
                    :fill="column.length === 1"
                />
              </div>
            </div>
          </transition>
        </main>
        <aside class="tv-side">
          <tv-results-column :results="view.ticker" :fresh-ids="freshIds"/>
          <tv-coming-up-column v-if="showsComingUp" :matches="upcoming" :limit="3"/>
          <tv-club-join-card v-if="view.clubJoin" :code="view.clubJoin" :club-name="view.clubName"/>
        </aside>
      </div>
      <tv-ticker
          v-if="view.showTicker"
          :results="view.ticker"
          :live="view.showLiveScores ? view.live : []"
          :fresh-ids="freshIds"
      />
      <tv-result-sting
          v-if="sting"
          :key="sting.result.fixtureId + ':' + sting.confirmed"
          :result="sting.result"
          :confirmed="sting.confirmed"
          :accent="view.accentColour"
          :canvas="confettiCanvas"
          @done="nextSting"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.tv-screen {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* A generous base so text that truncates (overflow hidden) keeps its descenders */
  line-height: 1.35;
  background: var(--tv-bg);
  color: var(--tv-ink);
  overflow: hidden;
}

.tv-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 640px;
  gap: 24px;
  padding: 24px;
}

.tv-feature {
  position: relative;
  min-width: 0;
  min-height: 0;
}

.tv-side {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
}

/* The results card takes whatever the sidebar has left */
.tv-side > :first-child {
  flex: 1 1 0;
}

.tv-side > :not(:first-child) {
  flex: 0 0 auto;
}

/* Portrait: the feature on top, the sidebar beneath it */
.tv-portrait .tv-body {
  grid-template-columns: minmax(0, 1fr);
  /* the sidebar gets a fixed share; its results card measures what fits */
  grid-template-rows: minmax(0, 1fr) 640px;
  gap: 24px;
}

/* Portrait sidebar: two columns, latest results down the left at full height, */
/* coming up and the club's join code stacked on the right */
.tv-portrait .tv-side {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-auto-rows: min-content;
  align-content: start;
}

.tv-portrait .tv-side > :first-child {
  grid-column: 1;
  grid-row: 1 / span 3;
  height: 640px;
}

.tv-portrait .tv-side > :not(:first-child) {
  grid-column: 2;
}

/* Results alone take the full width */
.tv-portrait .tv-side > :only-child {
  grid-column: 1 / -1;
}

.tv-screenful {
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
}

.tv-screenful--2 {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.tv-column {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.tv-progress-track {
  height: 4px;
  flex-shrink: 0;
  background: var(--tv-line);
}

/* How long until the feature area turns over */
.tv-progress {
  height: 100%;
  width: 100%;
  background: var(--tv-accent);
  transform-origin: left center;
  animation-name: tv-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  opacity: 0.85;
}

@keyframes tv-progress {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}

.tv-swap-enter-active,
.tv-swap-leave-active {
  transition: opacity 0.45s ease, transform 0.55s cubic-bezier(.2, .8, .2, 1);
}

.tv-swap-enter-from {
  opacity: 0;
  transform: translateX(80px);
}

.tv-swap-leave-to {
  opacity: 0;
  transform: translateX(-80px);
}


@media (prefers-reduced-motion: reduce) {
  .tv-swap-enter-active,
  .tv-swap-leave-active {
    transition: opacity 0.2s;
  }

  .tv-swap-enter-from,
  .tv-swap-leave-to {
    transform: none;
  }
}
</style>

<style lang="scss">
/* ── Clubhouse style ───────────────────────────────────────────────────────── */
/* The second look, laid over the same components and kept quiet: white */
/* surfaces, soft rounded cards with no borders, round tinted icons instead of */
/* slanted blocks, zebra rows instead of rules, and one typeface. The club */
/* colour is for accents only (icons, the progress line, winners, the leader). Selectors start .tv-screen.tv-screen--clubhouse so they outrank */
/* the components' own scoped rules. */
.tv-screen.tv-screen--clubhouse {
  .tv-body {
    gap: 28px;
    padding: 28px;
  }

  .tv-screenful,
  .tv-column,
  .tv-side {
    gap: 28px;
  }

  /* Top bar: a white panel over a thin club-colour line */
  .tv-top {
    background: var(--tv-panel);
    border-bottom: 0;
  }

  .tv-club-name,
  .tv-clock {
    color: var(--tv-ink);
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  .tv-live-bug {
    border-radius: 999px;
    font-family: 'Poppins', sans-serif;
    font-size: 20px;
    letter-spacing: 1px;
    padding: 6px 16px 6px 12px;
  }

  .tv-progress-track {
    height: 3px;
    background: var(--tv-line);
  }

  .tv-progress {
    background: var(--tv-accent);
    opacity: 1;
  }

  /* Cards */
  .tv-sec {
    border: 0;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08);
  }

  .tv-sec-bar {
    height: 76px;
    padding: 0 24px;
    gap: 14px;
    border-bottom: 0;
    background: transparent;
  }

  .tv-sec-icon {
    align-self: center;
    width: 46px;
    height: 46px;
    padding: 0;
    border-radius: 50%;
    clip-path: none;
    background: var(--tv-accent-tint);
    color: var(--tv-accent-text);

    .v-icon {
      font-size: 26px;
    }
  }

  .tv-sec-title {
    font-weight: 700;
    font-size: 30px;
    letter-spacing: -0.5px;
  }

  .tv-sec-sport {
    border: 0;
    border-radius: 999px;
    background: var(--tv-chip);
  }

  .tv-sec-body {
    padding: 0 24px 22px;
  }

  .tv-sec-foot {
    margin: 0 14px 14px;
    border-top: 0;
    border-radius: 8px;
    background: var(--tv-chip);
  }

  /* Tables and ladders: zebra rows, no rules */
  .tv-table th {
    border-bottom: 0;
    font-size: 18px;
  }

  .tv-table td,
  .tv-group-row,
  .tv-up-row,
  .tv-meet-row {
    border-bottom: 0;
  }

  .tv-table tbody tr:nth-child(even) td,
  .tv-group-row:nth-child(even),
  .tv-up-row:nth-child(even),
  .tv-meet-row:nth-child(even) {
    background: var(--tv-chip);
  }

  .tv-table td:first-child,
  .tv-up-row {
    border-radius: 6px 0 0 6px;
  }

  .tv-up-row,
  .tv-meet-row {
    border-radius: 6px;
  }

  .tv-chc-card {
    border: 0;
    border-radius: 6px;
    background: var(--tv-chip);
  }

  .tv-chc-side + .tv-chc-side {
    border-color: transparent;
  }

  .tv-table td:last-child {
    border-radius: 0 6px 6px 0;
  }

  .tv-num,
  .tv-pos,
  .tv-rung-pos {
    font-weight: 600;
  }

  .tv-table td {
    font-weight: 500;
  }

  /* Poppins is wider than the condensed numerals: give times more room */
  .tv-up-row {
    grid-template-columns: 250px 1fr auto;
    padding: 0 14px;
  }

  .tv-up-when {
    font-size: 22px;
    font-weight: 600;
  }

  .tv-num {
    font-size: 24px;
  }

  .tv-pos,
  .tv-rung-pos {
    font-size: 24px;
  }

  .tv-lead td {
    background: var(--tv-accent-tint) !important;
    box-shadow: none !important;
  }

  .tv-rung-ch {
    font-size: 22px;
  }

  /* Scorebugs: soft pills */
  .tv-bug {
    border: 0;
    border-radius: 6px;
    background: var(--tv-chip);
  }

  .tv-bug-cell.tv-bug--away,
  .tv-bug-game,
  .tv-bug-score {
    border-color: transparent;
  }

  .tv-bug-won {
    box-shadow: none;
  }

  .tv-bug-game,
  .tv-bug-score {
    font-size: calc(var(--bug-font) * 0.82);
  }

  .tv-bug-score {
    background: transparent;
  }

  .tv-bug-score--won {
    background: var(--tv-accent);
  }

  /* The crawl: a white strip, scorebugs on soft grey */
  .tv-ticker {
    background: var(--tv-panel);
    border-top: 1px solid var(--tv-line);
  }

  .tv-ticker-label {
    background: transparent;
    border-right: 1px solid var(--tv-line);
    padding-left: 28px;
  }

  .tv-ticker-icon {
    display: none;
  }

  .tv-ticker-title {
    color: var(--tv-ink);
    font-weight: 700;
    font-size: 28px;
  }

  /* The sting: a club-colour circle opens instead of a slanted wipe */
  .tv-sting-wipe {
    left: 0;
    width: 100%;
    transform: none;
    clip-path: circle(0% at 50% 50%);
    animation: tv-wipe-round 0.8s cubic-bezier(.7, 0, .2, 1) forwards;
  }

  .tv-sting-card {
    border-radius: 12px;
  }

  .tv-sting-kicker {
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  /* Strip and chip details */
  .tv-col-new,
  .tv-tick-live,
  .tv-live-tag,
  .tv-join-qr {
    border-radius: 999px;
  }

  .tv-join-qr {
    border-radius: 6px;
  }
}

@keyframes tv-wipe-round {
  to { clip-path: circle(75% at 50% 50%); }
}
</style>
